import React from 'react';
import {
  translate,
  Datagrid,
  Edit,
  Create,
  List,
  SimpleForm,
  TextField,
  TextInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  ReferenceArrayInput,
  SelectArrayInput,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  EditButton,
  Filter,
  NumberInput,
  BooleanInput,
  CreateButton,
  ExportButton,
  ArrayInput,
  SimpleFormIterator,
  BooleanField,
} from 'react-admin';
import Toolbar from '@material-ui/core/Toolbar';
import withStyles from '@material-ui/core/styles/withStyles';
import BookmarkIcon from '@material-ui/icons/Bookmark';

import resourceLocales from './locales';
import resourceConfig from './config';
import documentRowStyle from '../../../../utils/ui/documentRowStyle';
import DateInput from '../../../../components/DateInput';
import exporter from '../../../../utils/exporter';
import EnumInput from '../../../../components/EnumInput';
import EnumField from '../../../../components/EnumField';
import CheckButton from './CheckButton';
import LoadAllGoodsButton from './LoadAllGoodsButton';
import PostPagination from '../../../../utils/pagination';
import nkGoodsCheckGtin from '../nkGoodsCheckGtin';
import ExportDocButton from '../docH/ExportDocButton';

const ResourceIcon = BookmarkIcon;

const resourceToString = (x) => `#${x.id}`;

const trPrefix = `resources.${resourceConfig.name}`;

const listStyles = {};

const ResourceActions = ({
  bulkActions,
  basePath,
  currentSort,
  displayedFilters,
  exporter,
  filters,
  filterValues,
  onUnselectItems,
  resource,
  selectedIds,
  showFilter,
  total,
  translate,
}) => (
  <Toolbar>
    {bulkActions &&
      React.cloneElement(bulkActions, {
        basePath,
        filterValues,
        resource,
        selectedIds,
        onUnselectItems,
      })}
    {filters &&
      React.cloneElement(filters, {
        resource,
        showFilter,
        displayedFilters,
        filterValues,
        context: 'button',
      })}
    <nkGoodsCheckGtin.OpenButton />
    <CreateButton basePath={basePath} to={`${basePath}/create`} />
    <LoadAllGoodsButton />
    <ExportDocButton
      disabled={total === 0}
      resource={resource}
      sort={currentSort}
      filter={filterValues}
      exporter={exporter}
    />
  </Toolbar>
);

const ResourceListFilter = (props) => (
  <Filter {...props}>
    <NumberInput source={'brandId'} />
    <TextInput source={'brandName'} />
    <DateInput source={'date'} label={`resources.${resourceConfig.name}.fields.date`} />
    <TextInput source={'feedId'} />
    <DateInput
      source={'flagUpdatedDate'}
      label={`resources.${resourceConfig.name}.fields.flagUpdatedDate`}
    />
    <EnumInput
      source={'goodDetailedStatus'}
      translatePrefix={`${trPrefix}.enums.goodDetailedStatus.`}
      label={`${trPrefix}.fields.goodDetailedStatus`}
      enums={resourceConfig.enums.goodDetailedStatus}
    />
    <TextInput source={'goodId'} />
    <BooleanInput source={'goodMarkFlag'} />
    <TextInput source={'goodName'} />
    <NumberInput source={'goodRating'} />
    <BooleanInput source={'goodSigned'} />
    <TextInput source={'goodStatus'} />
    <BooleanInput source={'goodTurnFlag'} />
    <TextInput source={'goodUrl'} />
    <TextInput source={'gtin'} />
    <BooleanInput source={'kit'} />
    <BooleanInput source={'set'} />
    <TextInput source={'producerInn'} />
    <TextInput source={'producerName'} />
    <TextInput source={'remainderType'} />
    <TextInput source={'tnved'} />
    <DateInput
      source={'updatedDate'}
      label={`resources.${resourceConfig.name}.fields.updatedDate`}
    />
  </Filter>
);

const ResourceList = withStyles(listStyles)(({ classes, ...props }) => (
  <List
    {...props}
    filters={<ResourceListFilter />}
    sort={{ field: 'id', order: 'DESC' }}
    actions={<ResourceActions />}
    exporter={exporter}
    pagination={<PostPagination />}>
    <Datagrid rowStyle={documentRowStyle}>
      <TextField source={'id'} />
      <TextField source={'feedId'} />
      <TextField source={'goodStatus'} />
      <EnumField
        source={'goodDetailedStatus'}
        translatePrefix={`${trPrefix}.enums.goodDetailedStatus.`}
        label={`${trPrefix}.fields.goodDetailedStatus`}
        enums={resourceConfig.enums.goodDetailedStatus}
      />
      <TextField source={'goodId'} />
      <TextField source={'goodName'} />
      <BooleanField source={'goodSigned'} />
      <TextField source={'gtin'} />

      <CheckButton />
      <EditButton />
    </Datagrid>
  </List>
));

const ResourceTitle = translate(({ record, translate }) => (
  <span>
    {translate(`resources.${resourceConfig.name}.name`, { smart_count: 1 })} &quot;{record.id}&quot;
  </span>
));

const ResourceCreate = (props) => (
  <Create {...props}>
    <SimpleForm>
      <NumberInput source={'brandId'} />
      <TextInput source={'brandName'} />
      <DateInput source={'date'} label={`resources.${resourceConfig.name}.fields.date`} />
      <TextInput source={'feedId'} />
      <DateInput
        source={'flagUpdatedDate'}
        label={`resources.${resourceConfig.name}.fields.flagUpdatedDate`}
      />
      <EnumInput
        source={'goodDetailedStatus'}
        translatePrefix={`${trPrefix}.enums.goodDetailedStatus.`}
        label={`${trPrefix}.fields.goodDetailedStatus`}
        enums={resourceConfig.enums.goodDetailedStatus}
      />
      <TextInput source={'goodId'} />
      <BooleanInput source={'goodMarkFlag'} />
      <TextInput source={'goodName'} />
      <NumberInput source={'goodRating'} />
      <BooleanInput source={'goodSigned'} />
      <TextInput source={'goodStatus'} />
      <BooleanInput source={'goodTurnFlag'} />
      <TextInput source={'goodUrl'} />
      <TextInput source={'gtin'} />
      <BooleanInput source={'kit'} />
      <BooleanInput source={'set'} />
      <TextInput source={'producerInn'} />
      <TextInput source={'producerName'} />
      <TextInput source={'remainderType'} />
      <TextInput source={'tnved'} />
      <DateInput
        source={'updatedDate'}
        label={`resources.${resourceConfig.name}.fields.updatedDate`}
      />
      <ArrayInput source={'goodAttrs'}>
        <SimpleFormIterator>
          {/* <NumberInput source={'id'} /> */}
          <TextInput source={'attrId'} />
          <TextInput source={'attrValue'} />
          <TextInput source={'attrValueType'} />
          <NumberInput source={'attrGroupId'} />
          <TextInput source={'attrGroupName'} />
          <TextInput source={'attrName'} />
          <TextInput source={'gtin'} />
          <TextInput source={'level'} />
          <NumberInput source={'multiplier'} />
          <NumberInput source={'valueId'} />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source={'categories'}>
        <SimpleFormIterator>
          {/* <NumberInput source={'id'} /> */}
          <NumberInput source={'catId'} />
          <TextInput source={'catName'} />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source={'identifiedBy'}>
        <SimpleFormIterator>
          {/* <NumberInput source={'id'} /> */}
          <TextInput source={'level'} />
          <NumberInput source={'multiplier'} />
          <TextInput source={'type'} />
          <TextInput source={'value'} />
        </SimpleFormIterator>
      </ArrayInput>

      <ArrayInput source={'setGtins'}>
        <SimpleFormIterator>
          <NumberInput source={'id'} />
          <NumberInput source={'quantity'} />
          <TextInput source={'gtin'} />
        </SimpleFormIterator>
      </ArrayInput>
    </SimpleForm>
  </Create>
);

const ResourceEdit = (props) => (
  <Edit redirect={null} undoable={false} title={<ResourceTitle />} {...props}>
    <SimpleForm redirect={false}>
      <TextField source={'id'} />
      <NumberInput source={'brandId'} />
      <TextInput source={'brandName'} />
      <DateInput source={'date'} label={`resources.${resourceConfig.name}.fields.date`} />
      <TextInput source={'feedId'} />
      <DateInput
        source={'flagUpdatedDate'}
        label={`resources.${resourceConfig.name}.fields.flagUpdatedDate`}
      />
      <EnumInput
        source={'goodDetailedStatus'}
        translatePrefix={`${trPrefix}.enums.goodDetailedStatus.`}
        label={`${trPrefix}.fields.goodDetailedStatus`}
        enums={resourceConfig.enums.goodDetailedStatus}
      />
      <TextInput source={'goodId'} />
      <BooleanInput source={'goodMarkFlag'} />
      <TextInput source={'goodName'} />
      <NumberInput source={'goodRating'} />
      <BooleanInput source={'goodSigned'} />
      <TextInput source={'goodStatus'} />
      <BooleanInput source={'goodTurnFlag'} />
      <TextInput source={'goodUrl'} />
      <TextInput source={'gtin'} />
      <BooleanInput source={'kit'} />
      <BooleanInput source={'set'} />
      <TextInput source={'producerInn'} />
      <TextInput source={'producerName'} />
      <TextInput source={'remainderType'} />
      <TextInput source={'tnved'} />
      <DateInput
        source={'updatedDate'}
        label={`resources.${resourceConfig.name}.fields.updatedDate`}
      />
      <ArrayInput source={'goodAttrs'}>
        <SimpleFormIterator>
          {/* <NumberInput source={'id'} /> */}
          <TextInput source={'attrId'} />
          <TextInput source={'attrValue'} />
          <TextInput source={'attrValueType'} />
          <NumberInput source={'attrGroupId'} />
          <TextInput source={'attrGroupName'} />
          <TextInput source={'attrName'} />
          <TextInput source={'gtin'} />
          <TextInput source={'level'} />
          <NumberInput source={'multiplier'} />
          <NumberInput source={'valueId'} />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source={'categories'}>
        <SimpleFormIterator>
          {/* <NumberInput source={'id'} /> */}
          <NumberInput source={'catId'} />
          <TextInput source={'catName'} />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source={'identifiedBy'}>
        <SimpleFormIterator>
          {/* <NumberInput source={'id'} /> */}
          <TextInput source={'level'} />
          <NumberInput source={'multiplier'} />
          <TextInput source={'type'} />
          <TextInput source={'value'} />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source={'setGtins'}>
        <SimpleFormIterator>
          <NumberInput source={'id'} />
          <NumberInput source={'quantity'} />
          <TextInput source={'gtin'} />
        </SimpleFormIterator>
      </ArrayInput>
    </SimpleForm>
  </Edit>
);

const ResourceStringField = ({ record }) => <span>{resourceToString(record)}</span>;

const ResourceReferenceField = (props) => (
  <ReferenceField reference={resourceConfig.name} {...props}>
    <ResourceStringField />
  </ReferenceField>
);

const ResourceReferenceArrayField = (props) => (
  <ReferenceArrayField reference={resourceConfig.name} {...props}>
    <SingleFieldList>
      <ChipField source={'id'} />
    </SingleFieldList>
  </ReferenceArrayField>
);

const ResourceReferenceInput = (props) => (
  <ReferenceInput reference={resourceConfig.name} {...props}>
    <SelectInput optionText={resourceToString} />
  </ReferenceInput>
);

const ResourceReferenceArrayInput = (props) => (
  <ReferenceArrayInput reference={resourceConfig.name} {...props}>
    <SelectArrayInput optionText={resourceToString} />
  </ReferenceArrayInput>
);

export default {
  Icon: ResourceIcon,
  List: ResourceList,
  Title: ResourceTitle,
  Create: ResourceCreate,
  Edit: ResourceEdit,
  Field: ResourceReferenceField,
  ArrayField: ResourceReferenceArrayField,
  Input: ResourceReferenceInput,
  ArrayInput: ResourceReferenceArrayInput,

  name: resourceConfig.name,
  resourceConfig: {
    name: resourceConfig.name,
    list: ResourceList,
    edit: ResourceEdit,
    create: ResourceCreate,
    icon: ResourceIcon,
  },
  menuConfig: {
    name: resourceConfig.name,
    icon: ResourceIcon,
  },
  locales: resourceLocales,
  restProviderResolve: {
    name: resourceConfig.name,
    apiService: resourceConfig.apiService,
    basePath: resourceConfig.apiPath,
    schema: {
      date: {
        type: 'dateTime',
      },
      updatedDate: {
        type: 'dateTime',
      },
      flagUpdatedDate:{
        type: 'dateTime'
      }
    },
  },
};
